import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { useAuthValidation } from "../hooks/useAuthValidation";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Modal from "./Modal";
import CrisisModal from "./CrisisModal";
import DotMovement from "./DotMovement";
import DotContext from "../contexts/DotContext";
import { SessionThemeWrapper } from "../contexts/SessionThemeContext";
import MoodTracker from "./MoodTracker";
import ReadinessCheck from "./ReadinessCheck";
import { useAuth } from "../contexts/AuthContext";
import { useScriptFetcher } from "../hooks/useScriptFetcher";
import { useStepNavigation } from "../hooks/useStepNavigation";
import "./styles/GuidedSession.css";
import DynamicTitle from "./DynamicTitle";
import LoadingSkeleton from "./LoadingSkeleton";
import { trackEvent } from "../utils/posthog";
import { soundFileToOption } from "../utils/soundOptions";

function SessionPromptShell({ children, actions }) {
  return (
    <div className="fixed inset-0 z-40 bg-session-bg sm:bg-black/50 flex items-stretch sm:items-center justify-center sm:p-4">
      <div className="w-full sm:max-w-md bg-session-bg-elevated text-session-text sm:rounded-2xl sm:shadow-xl flex flex-col max-h-screen">
        <div className="flex-1 px-6 pt-10 pb-6 sm:py-8 overflow-y-auto">
          {children}
        </div>
        <div className="px-6 pb-6 sm:pb-8 sticky bottom-0 bg-session-bg-elevated border-t border-session-text-secondary/10 sm:border-0 pt-4 flex flex-col gap-2">
          {actions}
        </div>
      </div>
    </div>
  );
}

function ARTSession() {
  const navigate = useNavigate();
  const location = useLocation();
  const { scriptId } = useParams();
  const intakeData = location.state?.intakeData ?? null;
  const scriptIdentifier = scriptId || 'guided'; // Default to 'guided' slug for backwards compatibility
  const { user, isSubscribed, fetchUser } = useAuth();
  const isValidated = useAuthValidation();
  const { script, isLoading, error: scriptError, fetchScript } = useScriptFetcher(navigate);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [preSessionMood, setPreSessionMood] = useState(null);
  const [showReadinessCheck, setShowReadinessCheck] = useState(true);
  const [moodModalVisible, setMoodModalVisible] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const currentStepIndexRef = useRef(0);
  const totalStepsRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPauseMenu, setShowPauseMenu] = useState(false);
  const [cuesDismissedThisSession, setCuesDismissedThisSession] = useState(false);

  const {
    bgColor,
    dotColor,
    dotGlow,
    dotSize,
    dotSpeed,
    dotBrightness,
    isPaused,
    setIsPaused,
    setSidebarVisible,
    sidebarVisible,
    exitSession,
    setExitSession,
    soundFile,
    mute,
    volume,
    sessionCuesEnabled,
  } = useContext(DotContext);

  // Re-enabling "show cue text" clears a per-session dismissal so the cue returns.
  useEffect(() => {
    if (sessionCuesEnabled) {
      setCuesDismissedThisSession(false);
    }
  }, [sessionCuesEnabled]);

  useEffect(() => {
    if (!isValidated) {
      const currentPath = `/sessions/guided/${scriptIdentifier}`;
      if (!user) {
        navigate("/login", { replace: true, state: { from: currentPath } });
      } else if (!isSubscribed) {
        navigate("/subscribe", {
          replace: true,
          state: { from: currentPath },
        });
      }
    }
  }, [isValidated, user, isSubscribed, navigate, scriptIdentifier]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        // const decodedToken = jwtDecode(token);
        // setUserId(decodedToken.user_id);
      } catch (error) {
        toast.error("Invalid token. Please log in again.");
        localStorage.removeItem("token");
        navigate("/login", { replace: true });
      }
    } else {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!user || !isSubscribed) return;
    const abortController = new AbortController();

    const initSession = async () => {
      await fetchScript(scriptIdentifier);
    };

    initSession();

    return () => {
      abortController.abort();
    };
  }, [fetchScript, user, isSubscribed, scriptIdentifier]);

  // Keep refs in sync so cleanup callbacks can access the latest values
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Abandon session on tab close or component unmount
  useEffect(() => {
    const abandonSession = () => {
      const id = sessionIdRef.current;
      const token = localStorage.getItem("token");
      if (!id || !token) return;

      const url = `/api/sessions/${id}/abandon`;
      const body = JSON.stringify({
        steps_completed: (currentStepIndexRef.current || 0) + 1,
        total_steps: totalStepsRef.current,
      });

      try {
        fetch(url, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body,
          keepalive: true,
        });
      } catch {
        navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      }
    };

    window.addEventListener("beforeunload", abandonSession);
    return () => {
      window.removeEventListener("beforeunload", abandonSession);
      abandonSession();
    };
  }, []);

  // Show mood modal only when script is successfully loaded AND readiness check is done
  useEffect(() => {
    if (script && !scriptError && !isLoading && user && isSubscribed && !showReadinessCheck) {
      setMoodModalVisible(true);
    } else if (scriptError) {
      setMoodModalVisible(false);
    }
  }, [script, scriptError, isLoading, user, isSubscribed, showReadinessCheck]);

  // Handle script fetch errors - show error immediately and prevent session from starting
  useEffect(() => {
    if (scriptError) {
      // Check if it's a subscription-related error
      const isSubscriptionError = scriptError.toLowerCase().includes('subscription') || 
                                   scriptError.toLowerCase().includes('expired') ||
                                   scriptError.toLowerCase().includes('required');
      
      const errorMsg = isSubscriptionError 
        ? "Your subscription has expired or is not active. Please renew your subscription to continue."
        : scriptError || "Failed to load session script. Please try again.";
      
      toast.error(errorMsg, {
        autoClose: 5000,
      });
      // Prevent mood modal from showing
      setMoodModalVisible(false);
      // Refresh user subscription status in case it's out of sync
      if (fetchUser) {
        fetchUser().catch(err => {
          console.error("Failed to refresh user status:", err);
        });
      }
      // Navigate to subscribe page if subscription error, otherwise session selection
      const redirectPath = isSubscriptionError ? "/subscribe" : "/sessions";
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 3000);
    }
  }, [scriptError, navigate, fetchUser]);

  const onSessionEnd = useCallback(() => {
    setMoodModalVisible(true);
    setIsPaused(false);
  }, [setIsPaused]);

  const {
    currentStepIndex,
    setCurrentStepIndex,
    isModalVisible,
    setIsModalVisible,
    showDotMovement,
    setShowDotMovement,
    activeCueText,
    handleOptionClick,
    handleNextClick,
    handleSkipStep: skipStep,
    endDotMovement,
  } = useStepNavigation(script, onSessionEnd);

  // Keep step refs in sync so abandon callback can access latest values
  useEffect(() => {
    currentStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex]);

  useEffect(() => {
    totalStepsRef.current = script?.steps?.length || null;
  }, [script]);

  useEffect(() => {
    if (exitSession) {
      setShowDotMovement(false);
      setIsModalVisible(false);
      onSessionEnd();
      setExitSession(false);
    }
  }, [exitSession, setExitSession, onSessionEnd, setShowDotMovement, setIsModalVisible]);

  const handleRejectWarning = (rejectWarning) => {
    if (rejectWarning) {
      setIsWarningVisible(false);
      setIsModalVisible(true);
    } else {
      setIsWarningVisible(false);
      navigate("/emergency-support");
    }
  }

  const handleMoodSubmit = async (mood, note, type) => {
    if (isSubmitting) return;
    
    // Prevent session creation if script is not loaded
    if (type === "pre" && (!script || scriptError)) {
      toast.error("Session script not loaded. Please try again or contact support.");
      return;
    }
    
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token not found. Please log in again.");
        navigate("/login", { replace: true });
        return;
      }

      // Use script title as session type; show "Guided Session" for ART script in history
      const sessionType =
        script?.title === "Accelerated Resolution Therapy"
          ? "Guided Session"
          : (script?.title || "Guided");
      
      const payload =
        type === "pre"
          ? {
              user_id: user.id,
              mood_pre: mood,
              comment_pre: note,
              session_type: sessionType,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              ...(intakeData && {
                emotional_state: intakeData.emotionalState,
                time_preference: intakeData.timePreference,
                recommended_session_slug: intakeData.recommendedSlug,
                actual_session_slug: intakeData.actualSlug,
              }),
            }
          : {
              mood_post: mood,
              comment_post: note,
              background_color: bgColor,
              dot_color: dotColor,
              dot_size: dotSize,
              dot_speed: dotSpeed,
              sound_option: soundFileToOption(soundFile),
              volume: volume,
              session_cues_enabled: sessionCuesEnabled,
              steps_completed: (currentStepIndex || 0) + 1,
              total_steps: script?.steps?.length || null,
            };

      const apiURL =
        type === "pre" ? "/api/sessions" : `/api/sessions/${sessionId}`;
      const method = type === "pre" ? "post" : "patch";

      const response = await axios({
        method,
        url: apiURL,
        data: payload,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (mood <= 3) {
        console.log("Warning mood")
        setIsWarningVisible(true);
        setIsModalVisible(false);
      } else {
        setIsModalVisible(true)
      }

      if (type === "pre") {
        setSessionId(response.data.session_id);
        setMoodModalVisible(false);
        setPreSessionMood(mood);
        trackEvent("session_started", {
          session_type: sessionType,
          script_id: scriptIdentifier,
          mood_pre: mood,
          ...(intakeData && {
            emotional_state: intakeData.emotionalState,
            time_preference: intakeData.timePreference,
            was_recommended: intakeData.recommendedSlug === intakeData.actualSlug,
          }),
        });
      } else if (type === "post") {
        sessionIdRef.current = null;
        setSidebarVisible(false);
        trackEvent("session_completed", {
          session_type: sessionType,
          script_id: scriptIdentifier,
          mood_pre: preSessionMood,
          mood_post: mood,
          mood_delta: mood - preSessionMood,
        });
        navigate("/session-end");
      }
    } catch (error) {
      if (axios.isCancel(error)) return;
      if (error.response) {
        toast.error(`Error: ${error.response.data.message}`);
      } else if (error.request) {
        toast.error("Network error, please try again later.");
      } else {
        toast.error(`Error: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePause = () => {
    if (!isPaused) {
      // When pausing, show the pause menu
      setIsPaused(true);
      setShowPauseMenu(true);
      trackEvent("session_paused", {
        session_type: script?.title || "Guided",
        script_id: scriptIdentifier,
      });
    } else {
      // When resuming, hide the menu and resume
      setIsPaused(false);
      setShowPauseMenu(false);
    }
  };

  const handleResume = () => {
    setIsPaused(false);
    setShowPauseMenu(false);
  };

  const handleGoBackStep = () => {
    if (script && currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      setIsPaused(false);
      setShowPauseMenu(false);
      setShowDotMovement(false);
      setIsModalVisible(true);
    }
  };

  const handleSkipStep = () => {
    setIsPaused(false);
    setShowPauseMenu(false);
    skipStep();
  };

  const toggleNav = () => {
    console.log(bgColor);
    setSidebarVisible(!sidebarVisible);
  };

  const renderModalContent = () => {
    if (
      !script ||
      !script.steps ||
      script.steps.length === 0 ||
      currentStepIndex >= script.steps.length
    ) {
      return null;
    }

    const currentStep = script.steps[currentStepIndex];

    const promptText = (
      <p className="text-lg text-session-text leading-relaxed whitespace-pre-line">{currentStep.text}</p>
    );

    if (!isValidated) {
      return <SessionPromptShell actions={null}><p className="text-gray-500">Loading…</p></SessionPromptShell>;
    }

    const options = currentStep.options && currentStep.options.length > 0
      ? [...currentStep.options].sort((a, b) => a.id - b.id)
      : null;

    const actionButtons = options ? (
      options.map((option) => (
        <button
          key={option.id}
          onClick={() => handleOptionClick(option)}
          className="w-full bg-session-accent text-session-bg font-semibold py-4 rounded-xl hover:opacity-90 transition"
        >
          {option.text}
        </button>
      ))
    ) : (
      <button
        onClick={handleNextClick}
        className="w-full bg-session-accent text-session-bg font-semibold py-4 rounded-xl hover:opacity-90 transition"
      >
        Next
      </button>
    );

    return <SessionPromptShell actions={actionButtons}>{promptText}</SessionPromptShell>;
  };

  const handleReadinessComplete = () => {
    setShowReadinessCheck(false);
    // Mood modal will be shown by the useEffect when script is loaded
    if (script && !scriptError && !isLoading) {
      setMoodModalVisible(true);
    }
  };

  const handleReadinessCancel = () => {
    navigate("/sessions");
  };

  if (isLoading || !user || !isSubscribed) {
    return <LoadingSkeleton />;
  }

  if (scriptError) {
    return (
      <div className="guided-session-page flex items-center justify-center">
        <div className="text-center">
          <p className="text-text mb-4">{scriptError}</p>
          <p className="text-text mb-4">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Get display title from script or use default
  const sessionTitle = script?.title || "Guided Session";
  
  return (
    <SessionThemeWrapper className="guided-session-page bg-session-bg text-session-text">
      <DynamicTitle>{sessionTitle}</DynamicTitle>
      {!sidebarVisible && !showReadinessCheck && !moodModalVisible && !isModalVisible && (
        <button
          onClick={toggleNav}
          className="hamburger-menu"
          aria-label="toggle navigation"
        >
          <span className="hamburger" style={{ color: dotColor }}>&#9776;</span>
        </button>
      )}

      {showReadinessCheck && (
        <ReadinessCheck
          onContinue={handleReadinessComplete}
          onCancel={handleReadinessCancel}
        />
      )}

      {moodModalVisible && (
        <MoodTracker
          onSubmit={(mood, note) =>
            handleMoodSubmit(mood, note, preSessionMood ? "post" : "pre")
          }
          isPreSession={!preSessionMood}
        />
      )}

      {!showReadinessCheck && !moodModalVisible && isModalVisible && renderModalContent()}

      {isWarningVisible && (
        <CrisisModal 
          open={isWarningVisible} 
          onClose={() => setIsWarningVisible(false)} 
          handleRejectWarning={handleRejectWarning}
          isInCrisis={preSessionMood === 1}
        >
        </CrisisModal>  
      )}

      {showDotMovement && (
        <div className="dot-container">
          {/* In-session cue overlay: short phrase during dot movement, dismissible per session */}
          {activeCueText &&
            sessionCuesEnabled &&
            !cuesDismissedThisSession && (
              <div className="session-cue-overlay absolute inset-0 z-10 flex items-end justify-center pb-24 pointer-events-none group">
                {/* Pill background so cue text is readable on any dot/background color */}
                <button
                  type="button"
                  onClick={() => setCuesDismissedThisSession(true)}
                  aria-label="Dismiss cue"
                  className="session-cue-text px-4 py-2 rounded-full text-xl md:text-2xl font-normal select-none pointer-events-auto focus:outline-none"
                >
                  {activeCueText}
                </button>
              </div>
            )}
          <button
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-session-accent text-session-bg py-2 landscape:py-1 px-6 landscape:px-4 rounded hover:opacity-90 transition duration-200 z-50 font-bold landscape:text-sm pause-button-safe"
            onClick={togglePause}
          >
            {isPaused ? "Resume" : "Pause"}
          </button>
          <DotMovement
            shouldRun={showDotMovement}
            isPaused={isPaused}
            bgColor={bgColor}
            dotColor={dotColor}
            dotGlow={dotGlow}
            dotBrightness={dotBrightness}
            dotSize={dotSize}
            dotSpeed={dotSpeed}
            onCompletion={endDotMovement}
            soundFile={soundFile}
            mute={mute}
            volume={volume}
          />
        </div>
      )}

      {showPauseMenu && (
        <Modal open={showPauseMenu} onClose={() => { setShowPauseMenu(false); setIsPaused(false); }}>
          <SessionThemeWrapper>
            <div className="bg-session-bg-elevated text-session-text rounded-lg shadow-lg p-6 max-w-md mx-auto">
              <h2 className="text-xl font-bold mb-4">Session Paused</h2>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleResume}
                  className="bg-session-accent text-session-bg font-semibold py-2 px-4 rounded hover:opacity-90 transition"
                >
                  Resume
                </button>
                {script && currentStepIndex > 0 && (
                  <button
                    onClick={handleGoBackStep}
                    className="bg-session-bg text-session-text border border-session-text-secondary/20 font-semibold py-2 px-4 rounded hover:bg-session-bg/70 transition"
                  >
                    Go Back a Step
                  </button>
                )}
                {script && currentStepIndex < script.steps.length - 1 && (
                  <button
                    onClick={handleSkipStep}
                    className="bg-session-bg text-session-text border border-session-text-secondary/20 font-semibold py-2 px-4 rounded hover:bg-session-bg/70 transition"
                  >
                    Skip Step
                  </button>
                )}
              </div>
            </div>
          </SessionThemeWrapper>
        </Modal>
      )}
    </SessionThemeWrapper>
  );
}

export default ARTSession;
